import useAirPlusLmsLanguage from 'src/hooks/useAirPlusLmsLanguage';
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Redirect, useHistory, useLocation, Route } from 'react-router-dom';
import { ConfigProvider, Layout } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import koKR from 'antd/lib/locale/ko_KR';
import styled, { createGlobalStyle } from 'styled-components';

import AirPlusHeader from 'src/components/Header/AirPlusHeader';
import AirPlusLeftMenu from 'src/components/AirPlus/LeftMenu';
import AirTableHeaderTheme from 'src/components/AirPlus/TableHeaderTheme';
import useAirPlusResizeDiagnostics from 'src/components/AirPlus/resizeObserverDiagnostics';
import Loading from 'src/components/common/Loading';

import { useQueryProxy } from 'src/operations/proxy/user';
import { USER_ME } from 'src/operations/queries/getUser';
import { EXTERNAL_USER_STUDY_LEVELUP } from 'src/operations/queries/externalStudy';
import { calculateStudyStreak } from 'src/pages/AirPlus/studyDates';
import { isLogedIn, userInfoVar } from 'src/apollo/cache';
import { getCurrentToken } from '../utils';
import { useAuthCheck } from 'src/utils/hooks';
import { useQuery } from '@apollo/client';
import jwtDecode from 'jwt-decode';

const { Content } = Layout;
const MOBILE_VIEW_MAX_WIDTH = 768;
const SIDEBAR_STATE_CHANGE_EVENT = 'airPlusSidebarStateChange';

const isMobileViewport = () => typeof window !== 'undefined' &&
  (window.innerWidth <= MOBILE_VIEW_MAX_WIDTH || (window.innerWidth <= 1024 && window.innerHeight <= 600));

function useQueryParams() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const LanguageTransitionStyle = createGlobalStyle`
  ::view-transition-group(air-plus-language) { animation: none; }
  ::view-transition-old(air-plus-language) { animation: airLanguageOut 180ms ease both; mix-blend-mode: normal; }
  ::view-transition-new(air-plus-language) { animation: airLanguageIn 180ms ease both; mix-blend-mode: normal; }
  ::view-transition-old(root), ::view-transition-new(root) { animation: none; }
  @keyframes airLanguageOut { to { opacity: 0; } }
  @keyframes airLanguageIn { from { opacity: 0; } }
  @media(prefers-reduced-motion:reduce) {
    ::view-transition-old(air-plus-language), ::view-transition-new(air-plus-language) { animation: none; }
  }
`;

const LayoutWrapper = styled(Layout)`
  height: 100vh;
  height: 100dvh;
  min-width: 0;
  --air-header-height: 64px;
  overflow: hidden;
  background:
    radial-gradient(ellipse 800px 500px at 15% 30%, rgba(34, 211, 238, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 600px 400px at 85% 70%, rgba(16, 185, 129, 0.06) 0%, transparent 70%),
    radial-gradient(ellipse 500px 300px at 50% 10%, rgba(139, 92, 246, 0.04) 0%, transparent 70%),
    #e8f4f8;
  font-family: 'Nunito', Arial, 'Pretendard Variable', sans-serif;
  line-height: 1.5;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  view-transition-name: air-plus-language;

  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
    --air-header-height: 52px;
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    --air-mobile-title: 20px;
    --air-mobile-section: 18px;
    --air-mobile-book-title: 15px;
    --air-mobile-meta: 14px;
    --air-mobile-body: 13px;
    --air-mobile-caption: 11px;
    font-size: var(--air-mobile-body);
  }
`;

const BodyContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;
  overflow: hidden;
`;

const ContentContainer = styled(Content)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: ${({ $isReader }) => ($isReader ? 'hidden' : 'auto')};
  overflow-x: hidden;
  scrollbar-gutter: auto;
  -webkit-overflow-scrolling: touch;
  background: transparent;
  display: flex;
  flex-direction: column;

  > * { min-width: 0; flex-shrink: 0; }
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
    padding-bottom: env(safe-area-inset-bottom, 0px);
    scroll-padding-bottom: 16px;
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    scrollbar-gutter: auto;
  }
`;

function AirPlusLayoutInner({
  component: Component,
  logOut,
  isHeaderStatic = true,
  isDashboard = false,
  closeHeader = false,
  hideMenu = false,
  hideSearch = false,
  removeMargin = false,
  matchProps,
  ...rest
}) {

  useAirPlusResizeDiagnostics();
  const { language } = useAirPlusLmsLanguage();
  useEffect(() => {
    // Warm the local Korean font before the first toggle to avoid a late font swap.
    document.fonts?.load('600 14px "Pretendard Variable"', '한국어').catch(() => {});
  }, []);

  const history = useHistory();
  const queryParams = useQueryParams();
  const token = getCurrentToken();
  const [loaded, setLoaded] = useState(false);
  const { pathname } = useLocation();
  const isLmsRoute = pathname.startsWith('/air-plus/manage/');
  const previousIsLmsRoute = useRef(isLmsRoute);
  const [collapsed, setCollapsed] = useState(() => isLmsRoute || isMobileViewport());
  const [mobileViewport, setMobileViewport] = useState(isMobileViewport);

  const { loading, data, error } = useQueryProxy(USER_ME, {
    variables: { token },
    skip: !token,
  });

  const isReaderPage = /^\/air-plus\/(?:reader|epub-reader)\//.test(pathname);

  const isStudyOrImmersivePage =
    pathname.includes('/study') ||
    pathname.includes('/description/') ||
    isReaderPage ||
    pathname.includes('/fluency/') ||
    pathname.includes('/voca/') ||
    pathname.includes('/sentence/') ||
    pathname.includes('/quiz') ||
    pathname.includes('/matchinggame') ||
    pathname.includes('/memorygame') ||
    pathname.includes('/crosspuzzlegame') ||
    pathname.includes('/wordfallgame') ||
    pathname.includes('/bookreport') ||
    pathname.includes('/results/assignresult');

  const shouldHideMenu = hideMenu || isStudyOrImmersivePage;

  useEffect(() => {
    if (isMobileViewport()) setCollapsed(true);
  }, [pathname]);

  const userIdx = parseInt(data?.me?.idx || data?.me?.user_idx, 10);
  const shouldFetchStreak =
    !shouldHideMenu &&
    !isLmsRoute &&
    Boolean(userIdx) &&
    !Number.isNaN(userIdx);

  const { data: streakStudyData } = useQuery(EXTERNAL_USER_STUDY_LEVELUP, {
    variables: {
      type: 'air_plus',
      user_idx: userIdx,
      status_gte: 2,
      limit: 1000,
    },
    skip: !shouldFetchStreak,
  });

  const streakDays = useMemo(() => {
    const studies = streakStudyData?.getExternalStuiesList || [];
    const activityTimestamps = studies.map(
      (study) => study?.last_study_timestamp || study?.study_date,
    );

    return calculateStudyStreak(activityTimestamps);
  }, [streakStudyData]);

  useEffect(() => {
    if (error) {
      isLogedIn(false);
      if (pathname !== '/login') {
        history.replace('/login');
        return;
      }
      setLoaded(true);
    }
  }, [error, history, pathname]);

  useEffect(() => {
    if (!isLogedIn() || !token) {
      if (pathname !== '/login') {
        history.replace('/login');
      }
    }
  }, [history, token, pathname]);

  useEffect(() => {
    if (data?.me) {
      userInfoVar(data?.me);
      setLoaded(true);
    } else if (token) {
      userInfoVar(jwtDecode(token));
    }
  }, [data, loading, token]);

  useEffect(() => {
    const handleSidebarStateChange = ({ detail }) => {
      setCollapsed(detail);
    };

    window.addEventListener(SIDEBAR_STATE_CHANGE_EVENT, handleSidebarStateChange);

    return () => {
      window.removeEventListener(SIDEBAR_STATE_CHANGE_EVENT, handleSidebarStateChange);
    };
  }, []);

  useEffect(() => {
    if (previousIsLmsRoute.current !== isLmsRoute) {
      previousIsLmsRoute.current = isLmsRoute;
      setCollapsed(isLmsRoute);
    }
  }, [isLmsRoute]);

  const onCollapse = useCallback(() => {
    const nextCollapsed = !collapsed;
    setCollapsed(nextCollapsed);
    window.dispatchEvent(new CustomEvent(SIDEBAR_STATE_CHANGE_EVENT, { detail: nextCollapsed }));
  }, [collapsed]);

  useEffect(() => {
    const handleResize = () => {
      const nextMobile = isMobileViewport();
      setMobileViewport(nextMobile);
      if (nextMobile) setCollapsed(true);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isMobileViewport()) setCollapsed(true);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const { isAccepted } = useAuthCheck();

  if (isAccepted === false) {
    return <Redirect to="/" />;
  }

  if (loading || !loaded) {
    return <Loading />;
  }

  return (
    <ConfigProvider locale={language === 'ko' ? koKR : enUS}>
    <LayoutWrapper lang={language}>
      <LanguageTransitionStyle />
      <AirTableHeaderTheme />
      {!queryParams.get('removeHeader') && (
        <AirPlusHeader
          collapsed={collapsed}
          onCollapse={onCollapse}
          isStatic={isHeaderStatic}
          closeHeader={closeHeader}
          hideMenu={shouldHideMenu && !(isDashboard && [0, 1, 2, 6].includes(Number(data?.me?.type)))}
          isDashboard={isDashboard}
          hideSearch={hideSearch || pathname.startsWith('/air-plus/search')}
        />
      )}
      <BodyContainer>
        {!shouldHideMenu && <AirPlusLeftMenu collapsed={collapsed} streakDays={streakDays} mobile={mobileViewport} onClose={() => setCollapsed(true)} />}
        <ContentContainer data-air-plus-content $isReader={isReaderPage} $hasNavigation={!shouldHideMenu}>
          <Component {...matchProps} {...rest} />
        </ContentContainer>
      </BodyContainer>
    </LayoutWrapper>
    </ConfigProvider>
  );
}

function AirPlusLayout({
  component: Component,
  logOut,
  isHeaderStatic = true,
  closeHeader = false,
  hideMenu = false,
  hideSearch = false,
  removeMargin = false,
  isDashboard = false,
  ...rest
}) {
  return (
    <Route
      {...rest}
      render={(matchProps) => (
        <AirPlusLayoutInner
          component={Component}
          logOut={logOut}
          isHeaderStatic={isHeaderStatic}
          closeHeader={closeHeader}
          hideMenu={hideMenu}
          hideSearch={hideSearch}
          removeMargin={removeMargin}
          matchProps={matchProps}
          isDashboard={isDashboard}
          {...rest}
        />
      )}
    />
  );
}

export default AirPlusLayout;
